import React, { KeyboardEvent, MouseEvent, useMemo } from "react";
import { ButtonWithIcon } from "../buttons/button-with-icon/button-with-icon";
import { IconButton } from "../buttons/icon-button/icon-button";

import documentCheckIcon from "@src/assets/svg/document-check-icon.svg";
import uploadIcon from "@src/assets/svg/upload-icon.svg";
import deleteGrayIcon from "@src/assets/svg/delete-gray-icon.svg";
import styles from "./drop-area-add-source-gateway.module.scss";
import { useDropzone } from "react-dropzone";
import {
  acceptStyle,
  rejectStyle,
  stylesDropArea,
} from "./drop-area-add-source-gateway-dynamic-styles";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { useI18N } from "@src/language/language-context";

const cssClassName = "drop-area-add-source-gateway";

interface DropAreaAddSourceGatewayPropTypes<T, K extends keyof T> {
  label: string;
  values: T;
  keyProp: K;
  setValues: (values: T) => void;
}

export const DropAreaAddSourceGateway = <T, K extends keyof T>({
  label,
  values,
  keyProp,
  setValues,
}: DropAreaAddSourceGatewayPropTypes<T, K>) => {
  const i18n = useI18N();
  const texts = {
    uploadOrDragAndDropText: i18n("modules.dropArea.uploadOrDragAndDropText"),
  };

  const {
    getRootProps: getRootPropsClient,
    getInputProps: getInputPropsClient,
    isDragAccept: isDragAcceptClient,
    isDragReject: isDragRejectClient,
  } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length === 1) {
        const reader = new FileReader();
        reader.onload = function (e) {
          const base64String = (e.target.result as string).split(",")[1];
          setValues({
            ...values,
            [keyProp]: base64String as T[K],
            [`${String(keyProp)}FileName`]: acceptedFiles[0].name as string,
          });
        };
        reader.readAsDataURL(acceptedFiles[0]);
      }
    },
  });

  const styleClientDropArea = useMemo(
    () => ({
      ...stylesDropArea,
      ...(isDragAcceptClient ? acceptStyle : {}),
      ...(values[keyProp] ? acceptStyle : {}),
      ...(isDragRejectClient ? rejectStyle : {}),
    }),
    [isDragAcceptClient, isDragRejectClient],
  );

  const deleteUploadedFile = (
    e: MouseEvent | KeyboardEvent,
    certificateType: string,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (certificateType) {
      setValues({
        ...values,
        [keyProp]: "" as T[K],
        [`${String(keyProp)}FileName`]: "" as string,
      });
    }
  };

  return (
    <div className={styles[cssClassName]}>
      <label
        className={`${styles[`${cssClassName}__label`]} ${
          styles[`${cssClassName}__label--margin-bottom`]
        }`}
        htmlFor={label}
      >
        {label}
      </label>
      <span
        {...getRootPropsClient({
          style: styleClientDropArea,
        })}
      >
        <input {...getInputPropsClient()} id={label} />
        {values[keyProp] !== "" ? (
          <FlexRowContainer justifyContent="space-between">
            <ButtonWithIcon
              type="button"
              fun={() => ({})}
              icon={documentCheckIcon}
              variant={["no-outline", "color-grey", "font-thin"]}
              text={values[`${String(keyProp)}FileName`]}
            />
            <IconButton
              fun={(e) => deleteUploadedFile(e, "client")}
              icon={deleteGrayIcon}
            />
          </FlexRowContainer>
        ) : (
          <ButtonWithIcon
            type="button"
            fun={() => ({})}
            icon={uploadIcon}
            variant={["no-outline", "color-blue", "font-thin"]}
            text={texts.uploadOrDragAndDropText}
          />
        )}
      </span>
    </div>
  );
};
